import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { JobType } from "@/hooks/usePrinters";
import { JOB_TYPE_LABELS } from "@/hooks/usePrinters";
import { withRetry } from "@/lib/supabase-retry";

interface PrinterForJob {
  id: string;
  name: string;
  printer_name: string;
}

/**
 * Get the printer configured for a specific job type
 * Falls back to default printer if no specific printer is configured
 */
export async function getPrinterForJobType(
  restaurantId: string,
  jobType: JobType
): Promise<PrinterForJob | null> {
  if (!restaurantId) {
    console.warn('[print-routing] restaurantId not available, skipping print');
    return null;
  }
  try {
    // First, try to find a printer with this job type enabled - with retry
    let validPrinters: any[] = [];
    
    try {
      const jobTypeData = await withRetry(
        async () => {
          const { data, error } = await supabase
            .from("printer_job_types")
            .select(`
              printer_id,
              printers!inner (
                id,
                name,
                printer_name,
                is_active,
                restaurant_id
              )
            `)
            .eq("job_type", jobType)
            .eq("is_enabled", true);

          if (error) throw error;
          return data;
        },
        {
          maxRetries: 2,
          baseDelayMs: 300,
          onRetry: (attempt) => {
            console.warn(`[print-routing] Retry ${attempt}/2 fetching printer job types`);
          }
        }
      );

      // Filter by restaurant and active status
      validPrinters = jobTypeData?.filter(
        (jt: any) => 
          jt.printers?.restaurant_id === restaurantId && 
          jt.printers?.is_active === true
      ) || [];
    } catch (joinError: any) {
      // Handle 406 or other join errors with simplified approach
      console.warn("[print-routing] Join query failed, trying simplified approach:", joinError);
      
      try {
        // Step 1: Get printer IDs for this job type
        const { data: jobTypes } = await supabase
          .from("printer_job_types")
          .select("printer_id")
          .eq("job_type", jobType)
          .eq("is_enabled", true);

        if (jobTypes?.length) {
          // Step 2: Get the printer details
          const { data: printer } = await supabase
            .from("printers")
            .select("id, name, printer_name")
            .eq("id", jobTypes[0].printer_id)
            .eq("restaurant_id", restaurantId)
            .eq("is_active", true)
            .maybeSingle();
          
          if (printer) {
            return printer;
          }
        }
      } catch (fallbackError) {
        console.error("[print-routing] Fallback query also failed:", fallbackError);
      }
    }

    if (validPrinters.length > 0) {
      const printer = validPrinters[0].printers;
      return {
        id: printer.id,
        name: printer.name,
        printer_name: printer.printer_name,
      };
    }

    // Fallback to default printer - with retry
    try {
      const defaultPrinter = await withRetry(
        async () => {
          const { data, error } = await supabase
            .from("printers")
            .select("id, name, printer_name")
            .eq("restaurant_id", restaurantId)
            .eq("is_active", true)
            .eq("is_default", true)
            .maybeSingle();

          if (error) throw error;
          return data;
        },
        { maxRetries: 2, baseDelayMs: 200 }
      );

      if (defaultPrinter) {
        return defaultPrinter;
      }
    } catch (defaultError) {
      console.warn("[print-routing] Failed to fetch default printer:", defaultError);
    }

    // Fallback to any active printer
    try {
      const anyPrinter = await withRetry(
        async () => {
          const { data, error } = await supabase
            .from("printers")
            .select("id, name, printer_name")
            .eq("restaurant_id", restaurantId)
            .eq("is_active", true)
            .limit(1)
            .maybeSingle();

          if (error) throw error;
          return data;
        },
        { maxRetries: 2, baseDelayMs: 200 }
      );

      return anyPrinter || null;
    } catch (anyError) {
      console.warn("[print-routing] Failed to fetch any printer:", anyError);
      return null;
    }
  } catch (error) {
    console.error("Erro ao buscar impressora para o tipo de trabalho:", error);
    return null;
  }
}

/**
 * Get all printers configured for a specific job type
 */
export async function getAllPrintersForJobType(
  restaurantId: string,
  jobType: JobType
): Promise<PrinterForJob[]> {
  if (!restaurantId) {
    console.warn('[print-routing] restaurantId not available, skipping print');
    return [];
  }
  try {
    const { data: jobTypeData, error } = await supabase
      .from("printer_job_types")
      .select(`
        printer_id,
        printers!inner (
          id,
          name,
          printer_name,
          is_active,
          restaurant_id
        )
      `)
      .eq("job_type", jobType)
      .eq("is_enabled", true);

    if (error) throw error;

    const validPrinters = jobTypeData?.filter(
      (jt: any) =>
        jt.printers?.restaurant_id === restaurantId &&
        jt.printers?.is_active === true
    );

    return (validPrinters || []).map((jt: any) => ({
      id: jt.printers.id,
      name: jt.printers.name,
      printer_name: jt.printers.printer_name,
    }));
  } catch (error) {
    console.error("Erro ao buscar impressoras para o tipo de trabalho:", error);
    return [];
  }
}

/**
 * Get the printer name for printing (uses configured printer or fallback)
 * Shows notification if no printer is configured
 */
export async function getPrinterNameForJob(
  restaurantId: string,
  jobType: JobType,
  showWarning: boolean = true
): Promise<string | null> {
  if (!restaurantId) {
    console.warn('[print-routing] restaurantId not available, skipping print');
    return null;
  }
  const printer = await getPrinterForJobType(restaurantId, jobType);
  
  if (!printer && showWarning) {
    const jobLabel = JOB_TYPE_LABELS[jobType] || jobType;
    toast.warning(`Impressora não configurada para "${jobLabel}"`, {
      description: "Configure uma impressora em Configurações > Impressoras",
      duration: 5000,
      action: {
        label: "Configurar",
        onClick: () => {
          window.location.href = "/dashboard/impressoras";
        },
      },
    });
  }
  
  return printer?.printer_name || null;
}
